'use client'

import { useState } from 'react'
import Link from 'next/link'
import { CaretRightIcon } from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import type { Project, ProjectStatus } from '@/document'
import type { ProjectStats } from './select-project-stats'
import { TEXT_STYLES } from '@/ui/design-tokens'

interface ProjectListProps {
  projects: Project[]
  stats: ProjectStats
}

const STATUS_SECTIONS: {
  status: ProjectStatus
  label: string
  defaultOpen: boolean
}[] = [
  { status: 'active', label: 'Active', defaultOpen: true },
  { status: 'on_hold', label: 'On Hold', defaultOpen: false },
  { status: 'completed', label: 'Completed', defaultOpen: false },
]

function ProjectRow({
  project,
  stats,
}: {
  project: Project
  stats: ProjectStats
}) {
  const counts = stats[project.id] ?? {
    total: 0,
    completed: 0,
    percentage: 0,
    overdue: 0,
  }

  return (
    <Link
      href={`/app/projects/${project.id}`}
      className={cn(
        'border-border/30 hover:bg-muted/40 flex min-h-11 items-center justify-between rounded-md border px-4 py-2 [@media(pointer:fine)]:min-h-0',
        'transition-colors duration-150',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
      )}
      aria-label={`Open project: ${project.name}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm">{project.name}</p>
        {project.description && (
          <p className={cn(TEXT_STYLES.metaSmall, 'mt-0.5 truncate')}>
            {project.description}
          </p>
        )}
      </div>
      <div className="ml-3 flex shrink-0 items-center gap-2">
        <span className="text-muted-foreground text-xs tabular-nums">
          {counts.completed}/{counts.total}
        </span>
        {counts.total > 0 && (
          <div className="bg-border/50 h-1 w-12 overflow-hidden rounded-full">
            <div
              className="bg-accent h-full rounded-full"
              style={{ width: `${counts.percentage}%` }}
            />
          </div>
        )}
      </div>
    </Link>
  )
}

function StatusSection({
  label,
  projects,
  stats,
  defaultOpen,
}: {
  label: string
  projects: Project[]
  stats: ProjectStats
  defaultOpen: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)

  if (projects.length === 0) return null

  return (
    <section aria-label={`${label} projects`}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          TEXT_STYLES.sectionHeader,
          'flex w-full items-center gap-1.5 py-2 text-left',
          'focus-visible:ring-ring rounded-sm focus-visible:ring-2 focus-visible:outline-none',
        )}
        aria-expanded={open}
      >
        <CaretRightIcon
          className={cn(
            'h-3 w-3 transition-transform duration-150',
            open && 'rotate-90',
          )}
          weight="bold"
        />
        {label}
        <span className="text-muted-foreground/60 ml-1 text-xs font-normal">
          {projects.length}
        </span>
      </button>
      {open && (
        <div className="mt-1 space-y-1.5">
          {projects.map((project) => (
            <ProjectRow key={project.id} project={project} stats={stats} />
          ))}
        </div>
      )}
    </section>
  )
}

export function ProjectList({ projects, stats }: ProjectListProps) {
  if (projects.length === 0) {
    return (
      <div className="py-12 text-center" data-testid="projects-empty-state">
        <p className="text-muted-foreground text-sm">No projects yet.</p>
        <p className="text-muted-foreground/60 mt-1 text-xs">
          Create your first project to start organizing tasks.
        </p>
      </div>
    )
  }

  const grouped = STATUS_SECTIONS.map(({ status, label, defaultOpen }) => ({
    label,
    defaultOpen,
    projects: projects.filter((p) => p.status === status),
  }))

  return (
    <div className="space-y-4" data-testid="project-list">
      {grouped.map(({ label, defaultOpen, projects: sectionProjects }) => (
        <StatusSection
          key={label}
          label={label}
          projects={sectionProjects}
          stats={stats}
          defaultOpen={defaultOpen}
        />
      ))}
    </div>
  )
}
